import React, { useState } from 'react';
import { setFocus } from '../base/components/PropControl';
import StyleBlock from '../base/components/StyleBlock';
import Advert, { KAdFormat } from '../base/data/Advert';
import Campaign from '../base/data/Campaign';
import JSend from '../base/data/JSend';
import DataStore, { getDataPath } from '../base/plumbing/DataStore';
import { is, modifyHash, sum } from '../base/utils/miscutils';
import KWorkflow, { titleForWorkflow, WORKFLOW_STEPS } from './KWorkflow';
import { Alert, Card as BSCard, Col, Row } from 'reactstrap';
import KStatus from '../base/data/KStatus';

class Report extends JSend {
	/** where would you edit this? blank for this-page */
	workflow;
}

/**
 * 
 * @param {truthy} condition 
 * @param {?boolean} warn true for warning (vs error)
 * @returns {!string} ok|warning|error
 */
const checkItem = (condition, warn) => condition ? 'success' : (warn ? 'warning' : 'error');

// const HIGHLIGHT_PATH = ['widget','highlight'];

/** List of absolute necessities to display the advert */
function Checklist({ advert, workflow, workflowNum }) {
	if (!workflow) return null;
	// let highlight = DataStore.getValue(HIGHLIGHT_PATH);

	/** @type {Report[]} */
	let reports = [];
	// Pull out some fields for brevity / index-into-undefined protection
	const { branding = {}, advanced = {} } = advert;

	switch (workflow) {
	case KWorkflow.Goal:
		reports = reportOnGoal({ advert });
		break;
	case KWorkflow.DesignAd:
		reports = reportOnDesignVideo({ advert, branding });
		break;
	case KWorkflow.DesignPostEngagement:
		reports = reportOnDesignPostEngagement({ advert, branding });
		break;
	case KWorkflow.ImpactHub:
		reports = reportOnImpactHub({ advert, branding });
		break;
	case KWorkflow.Publish:
		reports = reportOnPublish({ advert, branding });
		break;
	}
	// TODO set stage status (green/yellow/red) for wizard bar
	// fraction done = 1 - errors/all
	// NB: guard against /0 = NaN
	// TODO This works for new ads - but for old ones, it means the % done starts low, then bounces up as you click through
	const pdoneForStage = DataStore.getValue(['widget', 'checklist', advert.id, 'pdone']) || DataStore.setValue(['widget', 'checklist', advert.id, 'pdone'], {}, false);
	pdoneForStage[workflow] = reports.length ? 1 - (reports.filter(rep => rep.status === 'error').length / reports.length) : 1;

	return (
		<div className="checklist">
			{reports.map((report, ri) =>
				<div key={ri} onClick={e => setHighlight(report.highlight, report.proppath)} className={JSend.status(report)}>
					{report.workflow && report.workflow !== workflow && <> (<a href={workflowLink(report.workflow)}>{report.workflow}</a>) </>}{JSend.message(report)}
				</div>
			)}
		</div>
	);
}


const setHighlight = (h, proppath) => {
	// TODO
	// DataStore.setValue(HIGHLIGHT_PATH, h);
	// setFocus(proppath);
	// if (h) {
	// 	$(h).focus();
	// }
};


const reportOnGoal = ({ advert }) => {
	let reports = [];
	reports.push({
		status: checkItem(advert.format), message: "Select goal and format"
	});
	reports.push({
		status: checkItem(advert.campaign), message: "Name the campaign",
		proppath: getDataPath(advert).concat("campaign")
	});
	reports.push({
		status: checkItem(advert.name, true), message: "Name the advert",
		proppath: getDataPath(advert).concat("name")
	});
	return reports;
};

const reportOnDesignVideo = ({ advert, branding }) => {
	let reports = [];
	reports.push({ status: checkItem(branding.logo || branding.logo_white), message: "Sponsor's logo" });

	/* eslint-disable react/no-array-index-key */
	const clist = Advert.charityList(advert);

	// have we actually got a charity, or are we just using a placeholder one to view the ad without any set?
	// (charityFound && placeholderCharity) || (!charityFound && !noPlaceholder)
	reports.push({ status: checkItem(clist.length), 
		message: "At least one charity" });
	
	if (clist.length) {
		const cids = {}; // for dupe detection
		clist.forEach((charity, index) => {
			const i1 = index + 1;
			if (!charity.name) {
				reports.push({ status: "warning", message: "Charity " + i1 + " needs a name" });
			}
			if (!charity.id) {
				reports.push({ status: "error", message: "Charity " + i1 + " needs an ID" });
			}
			if (!charity.logo && !charity.logo_white) {
				reports.push({ status: "error", message: "Charity " + i1 + " needs a logo" });
			}
			if (cids[charity.id]) {
				reports.push({ status: "error", message: `Charities ${cids[charity.id]} and ${i1} have same ID: ${charity.id}` });
			}
			cids[charity.id] = i1;
		});
	}

	// Social ads should have a splash video (for mock purposes) but don't need a main video at this stage
	if (KAdFormat.social === advert.format) {
		addSplashReport(advert, reports);
	} else {
		addVideoReport(advert, reports);
	}

	return reports;
};

/** Add report item for "does this advert have a video, and is it correctly set up?" */
const addVideoReport = (advert, reports) => {
	let videos = advert.videos || [];
	const hasVideo = videos.length && videos.find(video => video && video.url);
	reports.push({ status: checkItem(hasVideo), message: "At least one video" });
	if (hasVideo) {
		videos.forEach((video, index) => {
			if (!video.url) reports.push({ status: "error", message: `Video ${index + 1} needs video URL` });
			if (!video.aspect) reports.push({ status: "warning", message: `Video ${index + 1} should have aspect` });
			if (!video.duration && !video.videoSeconds) reports.push({ status: "warning", message: `Video ${index + 1} should have duration` });
		});
	}
};

/** Add report item for "does this social ad have a splash video or image so we can mock it up on the demo page?" */
const addSplashReport = (advert, reports) => {
	reports.push({ status: advert.mockSocialImage ? 'ok' : 'warning', message: 'Splash video or image' });
};


const reportOnDesignPostEngagement = ({ advert }) => {
	let reports = [];
	reports.push({ status: checkItem(advert.url), message: "Sponsor's landing page" });
	addVideoReport(advert, reports);
	return reports;
};


const reportOnImpactHub = ({ advert }) => {
	let pvc = Campaign.fetchFor(advert);
	let advanced = advert.advanced || {};
	/** @type {Report[]} */
	let reports = [];
	reports.push({
		status: pvc.value ? checkItem(pvc.value.bg) : "unknown",
		message: "A nice banner image",
		// highlight:".control-bg",
		// proppath: getDataPath({type:"Campaign", status:"DRAFT", id:Advert.campaign(advert)}).concat("bg")
	});
	if (pvc.value) {
		if (pvc.value.vertiser != advert.vertiser) {
			if (!pvc.value.vertiser) {
				// HACK patch Campaign.vertiser (though that might not get saved)
				console.warn("The Campaign (ID: " + pvc.value.id + ") does not have an Advertiser-ID set. Setting it locally to match this Advert's Advertiser-ID: " + advert.vertiser);
				pvc.value.vertiser = advert.vertiser;
			} else {
				reports.push({
					status: "error",
					message: "The Campaign (ID: " + pvc.value.id + ") is for Advertiser-ID: " + pvc.value.vertiser + " which does not match this Advert's Advertiser-ID: " + advert.vertiser,
				});
			}
		}
	}
	return reports;
};


const reportOnPublish = ({ advert }) => {
	let reports = [];
	reports.push({
		status: checkItem(advert.status===KStatus.PUBLISHED, true), 
		message: advert.status===KStatus.MODIFIED? "Publish updates" : "Publish (when you're ready)"
	});
	return reports;
};


export const workflowLink = (workflow) => {
	let wnum = WORKFLOW_STEPS.indexOf(workflow);
	return modifyHash(null, { workflow: wnum }, true);
};


export function AdvertWizardChecklist({ advert, workflow, workflowNum }) {
	const pdoneForStage = DataStore.getValue(['widget', 'checklist', advert.id, 'pdone']) || DataStore.setValue(['widget', 'checklist', advert.id, 'pdone'], {}, false);
	let pdone = sum(Object.values(pdoneForStage)) / WORKFLOW_STEPS.length;

	return (<div>
		<h3>Checklist</h3>
		{WORKFLOW_STEPS.map((w, i) =>
			w === workflow? 
				<BSCard body key={w}><b>{i + 1}. {titleForWorkflow(w, advert)} Checklist</b>
					<Checklist key={w} advert={advert} workflow={w} workflowNum={workflowNum} />
				</BSCard>
				: <div key={w} className="pl-3 pb-1"><a href={workflowLink(w)}>{i + 1}. {titleForWorkflow(w, advert)}</a></div>
		)}
		<hr/>
		<div>{Math.round(100 * pdone)}% done</div>
	</div>);
}

export default Checklist;
